import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { reportExportsList } from "../../../../services/eventReportService";
import { useDebounce } from "../../../../hooks/useDebounce";
import { handleFilterChange } from "../../../../helpers";
import Table from "../../../elements/table/Table";
import dayjs from "dayjs";
import LimitSelect from "../../../elements/LimitSelect";
import Search from "../../../form/Search";
import { useOrganization } from "../../../../context/OrganizationContext";
import { useTranslation } from "react-i18next";

const ExportsList = ({ queryKeys = [], type = "" }) => {
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganization();
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const { data, isLoading, isFetching } = useQuery({
    enabled: !!type && !!selectedOrganization,
    queryKey: [...queryKeys, filters, selectedOrganization, type],
    queryFn: async () => {
      const res = await reportExportsList({
        ...filters,
        ...(search && { keyword: search }),
        type,
        organization: selectedOrganization,
      });
      return res?.docs;
    },
  });

  const tableHeadData = [
    {
      _id: "Export",
      name: t("Export Id"),
      align: "left",
    },
    {
      _id: "name",
      name: t("name"),
      align: "left",
    },
    {
      _id: "type",
      name: t("Type"),
      align: "left",
    },
    {
      _id: "User",
      name: t("User"),
      align: "left",
    },
    {
      _id: "ExportOn",
      name: t("Export On"),
      align: "left",
    },
    {
      _id: "download",
      name: t("Download"),
      align: "right",
    },
  ];

  const tableData = data?.data?.map((item, i) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] text-purple-600 font-medium",
          data: item?.uid || "-",
        },
        {
          _id: "name",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] text-purple-600 font-medium",
          data: item?.name || "-",
        },
        {
          _id: "type",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] text-purple-600 font-medium",
          data: item?.type || "-",
        },
        {
          _id: "type",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] text-purple-600 font-medium",
          data:
            `${item?.userData?.firstName || ""} ${
              item?.userData?.lastName || ""
            }` || "-",
        },
        {
          _id: "type",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] text-purple-600 font-medium",
          data: dayjs(item?.createdAt).format("DD MMM, YYYY"),
        },
        {
          _id: "type",
          type: "text",
          align: "center",
          cellClass: "!w-[80px] text-purple-600 font-medium",
          data: item?.file?.url ? (
            <button
              type="button"
              className="w-8 h-8 rounded-md flex items-center justify-center mx-auto bg-slate-200 hover:bg-slate-200/50 transition-all duration-300 p-3"
              onClick={async () => {
                const response = await fetch(item?.file?.url, { mode: "cors" });
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = url;
                link.download = item?.name; // you can set dynamic filename
                document.body.appendChild(link);
                link.click();
                link.remove();

                window.URL.revokeObjectURL(url);
              }}
            >
              <i className="fa-fw fa-regular fa-download"></i>
            </button>
          ) : (
            ""
          ),
        },
      ],
    };
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row items-center gap-3">
        <div className="w-full md:w-36">
          <LimitSelect
            value={filters?.limit}
            allValue={data?.metadata?.totalDocs}
            onChange={(val) => handleFilterChange("limit", val, setFilters)}
          />
        </div>
        <div className="w-full md:w-72">
          <Search
            placeholder={t("Search here")}
            search={(val) => {
              setKeyword(val);
              handleFilterChange("page", 1, setFilters);
            }}
          />
        </div>
      </div>
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
        }}
        originalData={[...(data?.data || [])]}
        isFetching={isFetching}
        tableActionDropdownContainer={"!w-48"}
      />
    </div>
  );
};

export default ExportsList;
